import * as THREE from 'three'
import { COURT, LINES, LINES_FIBA, HOOP, SURFACE, THREE_PT } from '../constants.js'
import { zoneTexTransform, tileGridLines, snapKeyToGrid, plateExtent, scaledArcRadius, threePtUndersized, laneTicks } from './courtGrid.js'
import { dimsSpec } from './courtDims.js'
import { boardFrontZ } from '../hoops/hoopPosition.js'

// ============================================================================
//  Court — nawierzchnia jako 4 barwione strefy + linie FIBA.
//
//  Układ świata: linia końcowa na Z=0, boisko rośnie w +Z, środek X=0.
//  Strefy (od dołu): PŁYTA/WYBIEG (pełna płyta) < KORT < TRUMNA < LINIE — nakładane
//  warstwowo z mikro-offsetem w Y, więc brak szczeliny tła między płytami.
//  Po obwodzie płyty, NA ZEWNĄTRZ płytek, leży OBRZEŻE — wąska ramka (szerokość
//  z modelu, w mm) we własnym kolorze, odwzorowująca fizyczny element brzegowy
//  nawierzchni modułowej; wymiar zewnętrzny boiska = płytki + 2·obrzeże.
//
//  Barwienie: jedna szara (zdesaturowana) tekstura faktury per model, sklonowana
//  per strefa z własnym `repeat` (= wymiar strefy / rozmiar płytki) i barwiona
//  material.color = swatch. Liczba płytek NIE zwiększa liczby polygonów.
//
//  Linie: grupa obrócona o -90° wokół X; w niej localX = worldX, localY = -worldZ.
//  Linie są adaptacyjne (skalują się/przycinają do wymiarów) i mają wypełnione
//  złącza (przedłużanie odcinków o ext, nachodzenie prostych na łuki).
// ============================================================================

const Y_COURT = 0.004   // kort tuż nad płytą bazową
const Y_KEY = 0.008     // trumna nad kortem
const Y_GRID = 0.012    // siatka kafli 2D — nad strefami, pod liniami
const Y_LINES = 0.016   // linie na wierzchu
const Y_SHADOW = 0.02   // łapacz cienia na samym wierzchu (nad liniami)
// Grubość płyty i obrzeża = grubość płytki (z modelu, w mm). Płyta i obrzeże leżą
// koplanarnie (lico na Y=0), więc cała nawierzchnia ma jednolitą, realną grubość.

// Domyślna maska linii dla rebuild() bez argumentu `lines` (zgodność wstecz —
// odwzorowuje render sprzed wprowadzenia wyboru linii: junior wyłączony).
const ALL_LINES = { key: true, ftHalf: false, ftCircle: true, ticks: true, three: true, threeJr: false, noCharge: true }

export class Court {
  constructor() {
    this.group = new THREE.Group()
    this.group.name = 'Court'
    this.bounds = { width: 8, length: 8, centerZ: 3 }

    // Materiały stref — barwa ustawiana przez recolor; map przez rebuild.
    // MeshBasic + toneMapped:false → piksel == swatch (pełne WYSIWYG): kolor z
    // palety trafia na ekran 1:1, bez wpływu świateł, IBL i tone mappingu, które
    // wcześniej odsycały i przyciemniały barwę (efekt „blade/ciemne/fiolet").
    // Świadoma rezygnacja z cieniowania na samej nawierzchni na rzecz wierności
    // koloru — faktura kratki i spoiny zostają widoczne przez map.
    const zoneMat = () => new THREE.MeshBasicMaterial({ color: 0xffffff, toneMapped: false })
    this.matRunoff = zoneMat()
    this.matCourt = zoneMat()
    this.matKey = zoneMat()
    // Obrzeże — fizyczna ramka brzegowa o fakturze płytki (kratka). Szablon materiału;
    // każda z 4 belek dostaje własny klon (osobny `repeat` tekstury). Klony trzymamy
    // w _edgeMats, by recolor mógł je przebarwić bez przebudowy geometrii.
    this.matEdge = zoneMat()
    this._edgeMats = []

    // Siła faktury wg zoomu — JEDEN współdzielony uniform dla wszystkich stref (ta sama
    // referencja trafia do każdego shadera w onBeforeCompile), więc setTextureStrength
    // aktualizuje całą nawierzchnię jednym przypisaniem. Czytany co klatkę z main.
    this._tileUniform = { value: SURFACE.TILE_TEX_MAX }
    this.lineMat = new THREE.MeshStandardMaterial({
      color: 0xffffff, roughness: 0.5, metalness: 0,
      transparent: true, opacity: SURFACE.LINE_PAINT_OPACITY, // faktura przebija przez farbę
    })

    // Drugi materiał linii — łuk 3pt junior dostaje własny kolor (rozróżnialność
    // dwóch łuków). Klon lineMat z osobną barwą (ustawianą w recolor).
    this.lineMatJr = this.lineMat.clone()
    this.lineMatJr.color.set(0xff00ff) // magenta — placeholder do pierwszego recolor

    // Trzeci materiał linii — łuk 3pt senior też ma własny kolor (swatch pod
    // wierszem linii w sekcji „Linie"). Ten sam wzorzec co junior.
    this.lineMatThree = this.lineMat.clone()
    this.lineMatThree.color.set(0xff00ff) // placeholder do pierwszego recolor

    this._zoneGroup = new THREE.Group()
    this.group.add(this._zoneGroup)

    this._lineGroup = new THREE.Group()
    this._lineGroup.rotation.x = -Math.PI / 2
    this._lineGroup.position.y = Y_LINES
    this.group.add(this._lineGroup)

    // Siatka kafli — nakładka widoczna TYLKO w 2D (w 3D fakturę niesie tekstura).
    // Linie spoin w fazie kortu, na całej fizycznej płycie. Materiał współdzielony
    // (jak lineMat) — nie disposujemy go per rebuild; depthWrite:false, by malowane
    // linie FIBA (wyżej) zostały na wierzchu.
    this._gridGroup = new THREE.Group()
    this._gridGroup.rotation.x = -Math.PI / 2
    this._gridGroup.position.y = Y_GRID
    this._gridGroup.visible = false
    this.group.add(this._gridGroup)
    this._gridMat = new THREE.LineBasicMaterial({
      color: 0x222222, transparent: true, opacity: 0.28, depthWrite: false,
    })

    // Wymiarowanie 2D — linie wymiarowe z etykietami (jak na layoutach
    // produkcyjnych), widoczne TYLKO w widoku 2D. Materiał współdzielony jak
    // _gridMat; geometrie/tekstury etykiet idą przez _owned (dispose w rebuild).
    this._dimsGroup = new THREE.Group()
    this._dimsGroup.visible = false
    this.group.add(this._dimsGroup)
    this._dimMat = new THREE.MeshBasicMaterial({ color: 0xb03028, toneMapped: false })

    // Stan widoku: w 2D płyta jest „płaska" (faktura wygaszona), z siatką zamiast tekstury.
    this._view = '3d'
    this._flat = false

    this._owned = [] // geometrie i klony tekstur do dispose przy rebuild
    this._buildGlow()
    this._buildShadowCatcher()
  }

  // ----- Pełna przebudowa geometrii stref + linii -----
  // Płyta (widthTiles/lengthTiles/runoffTiles) steruje powierzchnią, kadrem i cieniem.
  // Obrys (lineW/lineL w metrach + lineSpec) steruje malowanymi liniami — niezależnie
  // od siatki płytek. Brak lineW/lineL ⇒ fallback do krawędzi płyty (= tryb ręczny).
  // rimZ jest per MODEL kosza (hoops.json: rimFromBaseline — kosze różnią się
  // wysięgiem); fallback do wartości HoopAir D72 (stała globalna).
  // hoopOffsetM — przesunięcie kosza suwakiem (wielokrotność płytki, w metrach):
  // rimZ przychodzi już PO przesunięciu, a offset wydłuża (w głąb) lub skraca
  // (za linię) trumnę — linia rzutów wolnych trzyma stałą relację do obręczy.
  rebuild({ grayTex, tileMm, widthTiles, lengthTiles, runoffTiles, lineW, lineL, lineSpec = 'compact', lines = ALL_LINES, edge = 0, tileThickMm = 16, rimZ = HOOP.RIM_FROM_BASELINE, hoopOffsetM = 0 }) {
    this._clear()

    const tileM = tileMm / 1000
    const courtW = widthTiles * tileM
    const courtL = lengthTiles * tileM
    const runoffM = runoffTiles * tileM
    // Referencja siatki dla zoneTexTransform/tileGridLines (kort = faza wszystkich stref).
    this._courtW = courtW
    this._tileM = tileM
    const edgeM = edge / 1000          // szerokość obrzeża (z modelu, w mm)
    const edgeH = tileThickMm / 1000   // grubość płyty i obrzeża = grubość płytki
    const spec = lineSpec === 'fiba' ? LINES_FIBA : LINES

    const lnW = lineW ?? courtW
    const lnL = lineL ?? courtL

    // Wybieg równy WSZĘDZIE dookoła — także za linią końcową (bez minimum na
    // maszt): przy małym/zerowym wybiegu słup kosza może stać poza płytą.
    const { fullW, fullL, centerZ } = plateExtent({ courtW, courtL, runoffM })

    // Obrzeże ZAWSZE na zewnątrz płytek — opaska wokół płyty, wymiar zewnętrzny
    // full + 2·edge (jak na layoutach produkcyjnych klienta: wybieg [N szt]
    // mierzy się od wewnętrznej krawędzi obrzeża do pola gry, a wymiar boiska
    // podaje się z obrzeżem — uwaga 04.09.2026; wcześniej przy wybiegu > 0
    // obrzeże zachodziło na zewnętrzny pas wybiegu, zaniżając gabaryt).
    const frameW = fullW + 2 * edgeM
    const frameL = fullL + 2 * edgeM

    this.bounds = { width: frameW, length: frameL, centerZ }

    // --- Płyta bazowa: slab o grubości = grubości płytki, lico na Y=0; pełne
    //     płytki kortu i wybiegu, obrzeże dochodzi z zewnątrz — jedna grubość ---
    this._applyTex(this.matRunoff, grayTex, { cx: 0, cz: centerZ, w: fullW, h: fullL })
    const runoffGeo = new THREE.BoxGeometry(fullW, edgeH, fullL)
    this._owned.push(runoffGeo)
    const runoff = new THREE.Mesh(runoffGeo, this.matRunoff)
    runoff.position.set(0, -edgeH / 2, centerZ)
    runoff.receiveShadow = true
    this._zoneGroup.add(runoff)

    // --- Obrzeże: ramka obwodowa koplanarna z płytą (ta sama grubość, faktura kratki) ---
    this._buildEdge(frameW, frameL, centerZ, edgeM, edgeH, grayTex)

    // --- Kort: siatka płytek widthTiles × lengthTiles na płycie; wybieg odsłonięty wokół ---
    this._applyTex(this.matCourt, grayTex, { cx: 0, cz: courtL / 2, w: courtW, h: courtL })
    this._zoneGroup.add(this._plane(courtW, courtL, courtL / 2, Y_COURT, this.matCourt))

    // --- Trumna (pole): wymiar regulaminowy (przycięty do obrysu) dociągnięty W DÓŁ
    //     do pełnych płytek siatki kortu — brzegi na spoinach, bez rozcinania płytek.
    //     Ten sam keyW/keyL steruje strefą koloru I liniami pola (spójność). ---
    const { keyW, keyL } = snapKeyToGrid({
      keyWTarget: Math.min(spec.KEY_WIDTH, lnW),
      // Trumna idzie za koszem: offset wydłuża ją w głąb boiska albo skraca
      // przy cofnięciu kosza za linię (linia RW w stałej relacji do obręczy).
      keyLTarget: Math.min(Math.max(0, spec.FREE_THROW_FROM_BASELINE + hoopOffsetM), lnL),
      tileM,
      widthTiles,
    })
    this._applyTex(this.matKey, grayTex, { cx: 0, cz: keyL / 2, w: keyW, h: keyL })
    this._zoneGroup.add(this._plane(keyW, keyL, keyL / 2, Y_KEY, this.matKey))

    // --- Linie (adaptacyjne, wypełnione złącza) — na obrysie lnW×lnL wg spec,
    //     pole/RW na dociągniętym keyW/keyL (jak strefa koloru) ---
    this._buildLines(lnW, lnL, spec, lines, keyW, keyL, rimZ, hoopOffsetM)

    // --- Siatka kafli 2D na całej płycie z płytek (widoczna tylko w widoku 2D) ---
    this._buildGrid(fullW, fullL, centerZ)

    // --- Wymiarowanie 2D: boisko zewnętrznie Z OBRZEŻEM, pole gry, wybieg,
    //     trumna (m + sztuki) ---
    this._buildDims({
      widthTiles, lengthTiles, runoffTiles,
      keyWTiles: Math.round(keyW / tileM), keyLTiles: Math.round(keyL / tileM),
      tileM, fullW, fullL, centerZ, edgeM,
    })

    // --- Cień-glow pod płytą (lekko większy od boiska; z obrzeżem, gdy wystaje) ---
    this._glow.scale.set(frameW * 1.28, frameL * 1.28, 1)
    this._glow.position.set(0, -edgeH - 0.005, centerZ)

    // --- Łapacz cienia na całej nawierzchni (piłka/kosz rzucają realny cień) ---
    this._shadow.scale.set(frameW, frameL, 1)
    this._shadow.position.set(0, Y_SHADOW, centerZ)
  }

  // ----- Zmiana samych kolorów (bez przebudowy geometrii) -----
  // resolve(zoneKey, colorId) — paleta jest per STREFA (linie mają własny zakres).
  recolor(colors, resolve) {
    this.matCourt.color.set(resolve('court', colors.court))
    this.matKey.color.set(resolve('key', colors.key))
    this.matRunoff.color.set(resolve('runoff', colors.runoff))
    const edgeCol = resolve('edge', colors.edge)
    this._edgeMats.forEach((m) => m.color.set(edgeCol))
    this.lineMat.color.set(resolve('lines', colors.lines))
    this.lineMatThree.color.set(resolve('linesThree', colors.linesThree))
    this.lineMatJr.color.set(resolve('linesJr', colors.linesJr))
  }

  // ===== prywatne: tekstury, płaszczyzny, sprzątanie =====

  // Klonuje szarą teksturę faktury i podpina do materiału strefy z repeat ORAZ
  // offsetem policzonym z położenia strefy w świecie (zoneTexTransform) — wszystkie
  // strefy dzielą JEDNĄ ciągłą siatkę kafli kortu (koniec przesunięć spoin trumny).
  // Strefy są unlit (MeshBasic) — bumpMap pominięty (relief wymaga światła, którego
  // tu nie ma); faktura kratki/spoiny niesie sama tekstura map.
  //   zone: { cx, cz, w, h } — środek (świat) i wymiary strefy (X=w, Z=h)
  _applyTex(mat, grayTex, zone) {
    const map = grayTex.map.clone()
    map.needsUpdate = true
    const t = zoneTexTransform(zone, { courtW: this._courtW, tileM: this._tileM })
    map.repeat.set(t.repeatX, t.repeatZ)
    map.offset.set(t.offsetX, t.offsetZ)
    this._owned.push(map)
    mat.map = map
    mat.needsUpdate = true
    this._patchTileVariation(mat)
  }

  // Buduje siatkę spoin (LineSegments) na prostokącie płyty: pełne linie kafli w
  // fazie kortu + obwód płyty. Widoczność steruje setView (tylko 2D).
  _buildGrid(plateW, plateL, centerZ) {
    this._gridGroup.clear()
    if (plateW <= 0 || plateL <= 0) return
    const minX = -plateW / 2
    const maxX = plateW / 2
    const minZ = centerZ - plateL / 2
    const maxZ = centerZ + plateL / 2
    const { xs, zs } = tileGridLines({ minX, maxX, minZ, maxZ }, { courtW: this._courtW, tileM: this._tileM })

    // Grupa obrócona o -90° (jak _lineGroup): punkt lokalny (worldX, -worldZ, 0).
    const pts = []
    for (const x of xs) pts.push(x, -minZ, 0, x, -maxZ, 0)             // pionowe (stałe X)
    for (const z of zs) pts.push(minX, -z, 0, maxX, -z, 0)            // poziome (stałe Z)
    // Obwód płyty (krawędź slabu) — domknięcie kratki.
    pts.push(minX, -minZ, 0, maxX, -minZ, 0, maxX, -minZ, 0, maxX, -maxZ, 0,
      maxX, -maxZ, 0, minX, -maxZ, 0, minX, -maxZ, 0, minX, -minZ, 0)

    const geo = new THREE.BufferGeometry()
    geo.setAttribute('position', new THREE.Float32BufferAttribute(pts, 3))
    this._owned.push(geo)
    this._gridGroup.add(new THREE.LineSegments(geo, this._gridMat))
    this._gridGroup.visible = this._view === '2d'
  }

  // Buduje linie wymiarowe (kreska + „wąsy" na końcach + etykieta-sprite) ze
  // specyfikacji dimsSpec. Widoczne tylko w 2D (setView), w czerwieni wymiarów
  // technicznych — jak na layoutach produkcyjnych Epufloor.
  _buildDims(data) {
    this._dimsGroup.clear()
    const spec = dimsSpec(data)
    const t = 0.03                                    // grubość kreski [m]
    const tick = Math.max(0.15, data.fullW * 0.012)   // długość wąsów końcowych
    const labelH = Math.max(0.4, data.fullW * 0.035)  // wysokość etykiety w świecie

    const bar = (x1, z1, x2, z2) => {
      const len = Math.hypot(x2 - x1, z2 - z1)
      if (len < 1e-6) return
      const geo = new THREE.PlaneGeometry(len, t)
      this._owned.push(geo)
      const m = new THREE.Mesh(geo, this._dimMat)
      // PlaneGeometry po rotation.x=-90°: lokalny kierunek (cosφ, sinφ) →
      // świat (cosφ, 0, -sinφ), więc φ = atan2(-dz, dx) układa kreskę wzdłuż
      // (dx, dz) w płaszczyźnie XZ (Euler 'XYZ': najpierw Z, potem X).
      m.rotation.set(-Math.PI / 2, 0, Math.atan2(-(z2 - z1), x2 - x1))
      m.position.set((x1 + x2) / 2, Y_GRID, (z1 + z2) / 2)
      this._dimsGroup.add(m)
    }

    for (const d of spec) {
      bar(d.x1, d.z1, d.x2, d.z2)
      // Wąsy prostopadłe na końcach (jak na rysunku technicznym).
      const dx = d.x2 - d.x1
      const dz = d.z2 - d.z1
      const len = Math.hypot(dx, dz) || 1
      const px = (-dz / len) * (tick / 2)
      const pz = (dx / len) * (tick / 2)
      bar(d.x1 - px, d.z1 - pz, d.x1 + px, d.z1 + pz)
      bar(d.x2 - px, d.z2 - pz, d.x2 + px, d.z2 + pz)
      this._dimsGroup.add(this._dimLabel(d, labelH))
    }
  }

  // Etykieta wymiaru: tekst z canvasa jako sprite (depthTest off — zawsze na
  // wierzchu), półprzezroczyste tło, by tekst był czytelny na kratce i liniach.
  _dimLabel(d, labelH) {
    const c = document.createElement('canvas')
    const font = '600 44px system-ui, sans-serif'
    const measure = c.getContext('2d')
    measure.font = font
    const pad = 16
    c.width = Math.ceil(measure.measureText(d.label).width) + pad * 2
    c.height = 64
    const ctx = c.getContext('2d') // zmiana width zresetowała stan kontekstu
    ctx.fillStyle = 'rgba(255,255,255,0.72)'
    ctx.fillRect(0, 0, c.width, c.height)
    ctx.font = font
    ctx.fillStyle = '#b03028'
    ctx.textBaseline = 'middle'
    ctx.fillText(d.label, pad, c.height / 2)
    const tex = new THREE.CanvasTexture(c)
    tex.colorSpace = THREE.SRGBColorSpace
    this._owned.push(tex)
    const mat = new THREE.SpriteMaterial({ map: tex, depthTest: false, toneMapped: false })
    this._owned.push(mat)
    const sprite = new THREE.Sprite(mat)
    sprite.scale.set(labelH * (c.width / c.height), labelH, 1)
    sprite.position.set((d.x1 + d.x2) / 2, Y_GRID + 0.01, (d.z1 + d.z2) / 2)
    return sprite
  }

  // Przełącza render pod widok. 2D: płaskie kolory (faktura wygaszona), siatka
  // kafli zamiast tekstury, bez cieni/poświaty. 3D: powrót do faktury i cieni.
  setView(view) {
    this._view = view
    const is2d = view === '2d'
    this._flat = is2d
    this._gridGroup.visible = is2d
    this._dimsGroup.visible = is2d
    this._shadow.visible = !is2d
    this._glow.visible = !is2d
    if (is2d) this._tileUniform.value = 0 // natychmiast płasko (setTextureStrength i tak utrzyma 0)
  }

  // Korekta kafelków w shaderze strefy (czysto na fragmencie):
  //   1. Siła faktury wg zoomu: mnożymy odchylenie tekstury od płaskiego lica przez
  //      uniform uTileStrength (0 = czysty swatch bez spoin, 1 = pełne spoiny). Wartość
  //      ustawia setTextureStrength wg dystansu kamery — z daleka 0 (płasko, bez migotania
  //      kratki), z bliska narasta. Uniform jest czytany CO KLATKĘ, więc strojenie działa na
  //      żywo (w odróżnieniu od stałych wpiekanych w GLSL, których onBeforeCompile nie odświeża).
  //   2. Stałe podbicie nasycenia (SURFACE.SATURATION = 130%) i jasności (BRIGHTNESS = 160%),
  //      żeby kolory były żywe. Operuje na diffuseColor (liniowo) — nasycenie miesza wokół
  //      luminancji Rec.709. Lift jest jednolity dla całej płyty (bez per-płytkowej wariacji).
  _patchTileVariation(mat) {
    const sat = SURFACE.SATURATION.toFixed(4)
    const val = SURFACE.BRIGHTNESS.toFixed(4)
    mat.onBeforeCompile = (shader) => {
      shader.uniforms.uTileStrength = this._tileUniform
      shader.fragmentShader = shader.fragmentShader
        .replace(
          '#include <common>',
          `#include <common>
          uniform float uTileStrength;`,
        )
        .replace(
          '#include <map_fragment>',
          `#ifdef USE_MAP
            vec4 tileTex = texture2D(map, vMapUv);
            // mnożnik 1.0 = płaskie lico (czysty swatch); tileTex = pełne spoiny.
            diffuseColor.rgb *= mix(vec3(1.0), tileTex.rgb, uTileStrength);
            diffuseColor.a *= tileTex.a;
          #endif
          {
            float lum = dot(diffuseColor.rgb, vec3(0.2126, 0.7152, 0.0722));
            diffuseColor.rgb = max(vec3(0.0), mix(vec3(lum), diffuseColor.rgb, ${sat}));
            diffuseColor.rgb *= ${val};
          }`,
        )
    }
    mat.needsUpdate = true
  }

  // Siła faktury wg dystansu kamery: blisko (≤ TILE_TEX_NEAR) → TILE_TEX_MAX (spoiny
  // widoczne), daleko (≥ TILE_TEX_FAR) → 0 (płaska nawierzchnia, bez migotania kratki).
  // Aktualizuje współdzielony uniform — działa na wszystkie strefy naraz. Wołane co klatkę.
  setTextureStrength(cameraDist) {
    if (this._flat) { this._tileUniform.value = 0; return } // 2D: płaskie kolory, siatka niesie kratkę
    const t = THREE.MathUtils.smoothstep(cameraDist, SURFACE.TILE_TEX_NEAR, SURFACE.TILE_TEX_FAR)
    this._tileUniform.value = SURFACE.TILE_TEX_MAX * (1 - t)
  }

  // Płaska płaszczyzna strefy (od Z=0 w +Z), wyśrodkowana w X.
  _plane(wX, wZ, cz, y, mat) {
    const geo = new THREE.PlaneGeometry(wX, wZ)
    this._owned.push(geo)
    const m = new THREE.Mesh(geo, mat)
    m.rotation.x = -Math.PI / 2
    m.position.set(0, y, cz)
    m.receiveShadow = true
    return m
  }

  // Obrzeże: ramka z 4 belek (box o grubości = grubości płytki) po obwodzie płyty —
  // przód/tył pełnej szerokości, boki między nimi. Pomijane gdy szerokość/grubość = 0.
  _buildEdge(fullW, fullL, centerZ, edgeM, edgeH, grayTex) {
    this._edgeMats = []
    if (edgeM <= 0 || edgeH <= 0) return
    const halfX = fullW / 2
    const zMin = centerZ - fullL / 2
    const zMax = centerZ + fullL / 2
    const innerL = Math.max(0, fullL - 2 * edgeM)
    this._edgeBar(fullW, edgeM, 0, zMin + edgeM / 2, edgeH, grayTex)            // tylna krawędź (Z min)
    this._edgeBar(fullW, edgeM, 0, zMax - edgeM / 2, edgeH, grayTex)            // przednia krawędź (Z max)
    this._edgeBar(edgeM, innerL, -(halfX - edgeM / 2), centerZ, edgeH, grayTex) // bok lewy
    this._edgeBar(edgeM, innerL, halfX - edgeM / 2, centerZ, edgeH, grayTex)    // bok prawy
  }

  // Pojedyncza belka obrzeża: box o grubości płytki, lico na Y=0 (koplanarne z płytą),
  // z fakturą kratki wyrównaną do wspólnej siatki kortu (offset z położenia belki).
  // Barwa przez recolor (przez _edgeMats).
  _edgeBar(wX, wZ, cx, cz, edgeH, grayTex) {
    const geo = new THREE.BoxGeometry(wX, edgeH, wZ)
    this._owned.push(geo)
    const mat = this.matEdge.clone()
    this._owned.push(mat)
    this._applyTex(mat, grayTex, { cx, cz, w: wX, h: wZ })
    this._edgeMats.push(mat)
    const m = new THREE.Mesh(geo, mat)
    m.position.set(cx, -edgeH / 2, cz)
    m.receiveShadow = true
    this._zoneGroup.add(m)
  }

  _clear() {
    this._owned.forEach((o) => o.dispose && o.dispose())
    this._owned = []
    this._zoneGroup.clear()  // materiały zostają, geometrie zdisposowane wyżej
    this._lineGroup.clear()
    this._gridGroup.clear()  // _gridMat współdzielony zostaje; geometria w _owned
    this._dimsGroup.clear()  // _dimMat współdzielony; geometrie/tekstury w _owned
  }

  // ===== prywatne: linie FIBA (lokalne XY; localY = -worldZ) =====

  // rimZ — środek obręczy od linii końcowej (per model kosza); łuki za 3 pkt
  // i półkole no-charge liczą się od niego.
  _buildLines(lineW, lineL, spec, lines, keyW, keyL, rimZ = HOOP.RIM_FROM_BASELINE, hoopOffsetM = 0) {
    const halfW = lineW / 2
    const len = lineL
    const lw = COURT.LINE_WIDTH
    const ext = lw / 2            // wydłużenie w złączach (lite narożniki)

    // Granice (obrys) — ZAWSZE rysowane, bez przełącznika.
    this._seg(-halfW, 0, halfW, 0, lw, ext)        // linia końcowa (z=0)
    this._seg(-halfW, -len, halfW, -len, lw, ext)  // tylna krawędź
    this._seg(-halfW, 0, -halfW, -len, lw, ext)    // boczna lewa
    this._seg(halfW, 0, halfW, -len, lw, ext)      // boczna prawa

    // Pole/RW na dociągniętym keyW/keyL (jak strefa koloru). Clamp do obrysu
    // (halfW - lw, len - lw) zostaje jako zabezpieczenie, by linia pola nie nałożyła
    // się na linię boczną/tylną. Koło RW i no-charge zależą od keyHalf nawet gdy
    // sam obrys pola jest ukryty.
    const keyHalf = Math.min(keyW / 2, halfW - lw)
    const ftZ = Math.min(keyL, len - lw)

    // Pole (trumna): obrys + linia rzutów wolnych.
    if (lines.key) {
      this._seg(-keyHalf, 0, -keyHalf, -ftZ, lw, ext)
      this._seg(keyHalf, 0, keyHalf, -ftZ, lw, ext)
      this._seg(-keyHalf, -ftZ, keyHalf, -ftZ, lw, ext)

      // Znaczniki na bokach pola (mają sens tylko z polem) — układ FIBA z
      // laneTicks: 3 wąskie kreski + blok neutral zone, wysunięte 0,10 m na
      // zewnątrz linii pola; idą za suwakiem kosza razem z trumną.
      if (lines.ticks) {
        const tickLen = 0.10
        for (const { z, depth } of laneTicks({ ftZ, offsetM: hoopOffsetM })) {
          if (depth > 0) {
            // Blok: wypełniony pas depth × tickLen (segment wzdłuż pola o szerokości tickLen).
            this._seg(-keyHalf - tickLen / 2, -z, -keyHalf - tickLen / 2, -(z + depth), tickLen)
            this._seg(keyHalf + tickLen / 2, -z, keyHalf + tickLen / 2, -(z + depth), tickLen)
          } else {
            this._seg(-keyHalf - tickLen, -z, -keyHalf, -z, lw)
            this._seg(keyHalf, -z, keyHalf + tickLen, -z, lw)
          }
        }
      }
    }

    // Rzuty wolne — dwa wykluczające się warianty (checkboxy gaszą się nawzajem,
    // lineToggle): „Półkole" = tylko CIĄGŁA połówka NA ZEWNĄTRZ trumny (domyślne,
    // jak na layoutach klienta); „Koło" = ta sama ciągła połówka na zewnątrz +
    // PRZERYWANA połówka WEWNĄTRZ trumny (w stronę kosza). Promień proporcjonalny
    // do realnej szerokości trumny (rośnie/maleje razem z nią). Układ lokalny:
    // localY = -worldZ, środek (0, -ftZ); kąty 0..π = strona kosza (wnętrze
    // trumny), π..2π = na zewnątrz trumny.
    if (lines.ftHalf || lines.ftCircle) {
      const ftR = Math.min(scaledArcRadius({ keyW, spec, base: spec.FREE_THROW_CIRCLE_R }), keyHalf)
      this._arc(0, -ftZ, ftR, lw, Math.PI, Math.PI)
      if (lines.ftCircle) this._dashedArc(0, -ftZ, ftR, lw, 0, Math.PI, 9)
    }

    // Łuki za 3 punkty — dwa niezależne, o STAŁYCH promieniach (THREE_PT).
    // Senior i junior mają własne materiały (lineMatThree/lineMatJr) — kolory
    // ustawiane swatchami pod wierszami linii w sekcji „Linie".
    if (lines.three) this._threePt(halfW, len, lw, ext, rimZ, spec.THREE_PT_SIDE_INSET, THREE_PT.SENIOR, this.lineMatThree)
    if (lines.threeJr) this._threePt(halfW, len, lw, ext, rimZ, spec.THREE_PT_SIDE_INSET, THREE_PT.JUNIOR, this.lineMatJr)

    // Dopasowanie linii za 3 pkt do obrysu — czytane przez UI (ikona ostrzeżenia
    // „niewymiarowa") i payload wyceny. true = pełnowymiarowa (regulaminowy
    // promień; przycięcie boczne na wąskim boisku nie flaguje — patrz courtGrid).
    this.lineFit = {
      three: !threePtUndersized({ len, rimZ, radius: THREE_PT.SENIOR, lineW: lw }),
      threeJr: !threePtUndersized({ len, rimZ, radius: THREE_PT.JUNIOR, lineW: lw }),
    }

    // Półkole no-charge pod koszem — skaluje się razem z trumną (spójnie z kołem RW).
    // Proste odcinki NIE dochodzą do linii końcowej: wg FIBA kończą się na
    // wysokości przedniej płaszczyzny tablicy (0,375 m przed środkiem obręczy),
    // więc mają stałą długość i idą za koszem razem z łukiem. Bez `ext` —
    // wolny koniec przy tablicy ma być dokładnie na wymiarze, a styk z łukiem
    // wypełnia sam łuk (jego początek jest styczny do odcinka).
    if (lines.noCharge) {
      const ncR = Math.min(scaledArcRadius({ keyW, spec, base: spec.NO_CHARGE_R }), keyHalf)
      const boardZ = boardFrontZ(rimZ)
      this._arc(0, -rimZ, ncR, lw, Math.PI, Math.PI)
      this._seg(-ncR, -boardZ, -ncR, -rimZ, lw)
      this._seg(ncR, -boardZ, ncR, -rimZ, lw)
    }

    this._lineGroup.traverse((o) => { if (o.isMesh) o.receiveShadow = true })
  }

  // Pojedynczy łuk za 3 pkt o zadanym promieniu i materiale. Logika bez zmian
  // względem pierwotnej (proste boczne dla szerokiego boiska vs docięcie do
  // krawędzi dla wąskiego); clamp R3 do len-lw-rimZ, by łuk nie wyszedł za tył.
  _threePt(halfW, len, lw, ext, rimZ, sideInset, radius, mat) {
    let R3 = radius
    const maxDz = len - lw - rimZ
    if (maxDz > 0) R3 = Math.min(R3, maxDz)

    if (halfW < R3) {
      // Łuk szerszy niż obrys — przytnij do linii bocznych (x = ±halfW).
      const ang = Math.acos(Math.min(1, halfW / R3))
      const aLeft = ang - Math.PI
      const aRight = -ang
      this._arc(0, -rimZ, R3, lw, aLeft, aRight - aLeft, mat)
    } else {
      // Kort dość szeroki — klasyczne rogi: proste boczne + łuk.
      const sideX = Math.min(halfW - sideInset, R3 - lw)
      const dz = Math.sqrt(Math.max(R3 * R3 - sideX * sideX, 0))
      const meetZ = rimZ + dz
      this._seg(-sideX, 0, -sideX, -meetZ, lw, ext, mat)
      this._seg(sideX, 0, sideX, -meetZ, lw, ext, mat)
      const aRight = Math.atan2(rimZ - meetZ, sideX)
      const aLeft = Math.atan2(rimZ - meetZ, -sideX)
      const over = 0.02 // nachodzenie łuku na proste (wypełnione styki)
      this._arc(0, -rimZ, R3, lw, aLeft - over, (aRight - aLeft) + 2 * over, mat)
    }
  }

  // Odcinek linii; ext wydłuża OBA końce (wypełnione złącza).
  _seg(x1, y1, x2, y2, w, ext = 0, mat = this.lineMat) {
    const dx = x2 - x1
    const dy = y2 - y1
    const len = Math.hypot(dx, dy)
    if (len < 1e-6) return
    const ux = dx / len
    const uy = dy / len
    const ax = x1 - ux * ext
    const ay = y1 - uy * ext
    const bx = x2 + ux * ext
    const by = y2 + uy * ext
    const geo = new THREE.PlaneGeometry(len + 2 * ext, w)
    this._owned.push(geo)
    const m = new THREE.Mesh(geo, mat)
    m.position.set((ax + bx) / 2, (ay + by) / 2, 0)
    m.rotation.z = Math.atan2(by - ay, bx - ax)
    this._lineGroup.add(m)
  }

  _arc(cx, cy, r, w, thetaStart, thetaLength, mat = this.lineMat) {
    const geo = new THREE.RingGeometry(r - w / 2, r + w / 2, 80, 1, thetaStart, thetaLength)
    this._owned.push(geo)
    const m = new THREE.Mesh(geo, mat)
    m.position.set(cx, cy, 0)
    this._lineGroup.add(m)
  }

  _dashedArc(cx, cy, r, w, thetaStart, thetaLength, n, mat = this.lineMat) {
    const gap = thetaLength / (n * 2 - 1)
    for (let i = 0; i < n; i++) this._arc(cx, cy, r, w, thetaStart + i * 2 * gap, gap, mat)
  }

  // ===== prywatne: cień-glow =====

  _buildGlow() {
    const s = 256
    const c = document.createElement('canvas')
    c.width = c.height = s
    const ctx = c.getContext('2d')
    const g = ctx.createRadialGradient(s / 2, s / 2, s * 0.05, s / 2, s / 2, s / 2)
    g.addColorStop(0.0, 'rgba(90,95,105,0.40)')
    g.addColorStop(0.5, 'rgba(90,95,105,0.20)')
    g.addColorStop(1.0, 'rgba(90,95,105,0.0)')
    ctx.fillStyle = g
    ctx.fillRect(0, 0, s, s)
    const tex = new THREE.CanvasTexture(c)
    tex.colorSpace = THREE.SRGBColorSpace
    const mat = new THREE.MeshBasicMaterial({ map: tex, transparent: true, depthWrite: false })
    this._glow = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), mat)
    this._glow.rotation.x = -Math.PI / 2
    this._glow.renderOrder = -1
    this.group.add(this._glow)
  }

  // ===== prywatne: łapacz cienia =====

  // Strefy nawierzchni są unlit (MeshBasic), więc nie odbierają cieni — ustawienie
  // receiveShadow na nich nie ma efektu. Realny cień piłki i kosza łapie osobna,
  // przezroczysta płaszczyzna z ShadowMaterial leżąca tuż nad nawierzchnią: renderuje
  // przyciemnienie WYŁĄCZNIE tam, gdzie pada cień, a poza nim jest w pełni przezroczysta —
  // barwy WYSIWYG stref zostają nietknięte. depthWrite=false, by nie przesłaniała geometrii
  // nad nią; renderOrder=1, by nakładała się po strefach i liniach. Skala/pozycja w rebuild().
  _buildShadowCatcher() {
    const mat = new THREE.ShadowMaterial({ opacity: SURFACE.SHADOW_OPACITY })
    mat.depthWrite = false
    this._shadow = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), mat)
    this._shadow.rotation.x = -Math.PI / 2
    this._shadow.position.y = Y_SHADOW
    this._shadow.receiveShadow = true
    this._shadow.renderOrder = 1
    this.group.add(this._shadow)
  }
}
